import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  IconButton,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNotify } from "react-admin";
import { userApi, organizationsApi } from "../../providers/env";
import { CreateUserModal } from "../../components/CreateUserModal";
import { EditUserModal } from "../../components/EditUserModal";
import { OrganizationOut, UserRead } from "../../generated";

interface UsersTabProps {
  organizationId: number | null;
  isOrgContext: boolean;
}

export const UsersTab: React.FC<UsersTabProps> = ({ organizationId, isOrgContext }) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserRead[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationOut[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRead | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [isSubAdmin, setIsSubAdmin] = useState(false);

  useEffect(() => {
    checkUserRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, isOrgContext]);

  const checkUserRole = () => {
    try {
      const permissions = localStorage.getItem("permissions");
      if (permissions) {
        const userInfo = JSON.parse(permissions);
        setIsSuperAdmin(userInfo.is_superuser || false);

        // Check if user is Org Admin or Sub Admin
        const roleName = userInfo.role?.name || "";
        setIsOrgAdmin(roleName === "Org Admin");
        setIsSubAdmin(roleName === "Sub Admin");

        if (isOrgContext && organizationId) {
          // Organization context: use provided organization ID
          setSelectedOrgId(String(organizationId));
          fetchOrganizationsList();
          fetchUsers(String(organizationId));
        } else if (userInfo.is_superuser && !isOrgContext) {
          // Super admin global view: show all organizations
          fetchOrganizations();
        } else if (userInfo.organization_id) {
          // Fallback: use user's organization
          setSelectedOrgId(userInfo.organization_id);
          fetchOrganizationsList();
          fetchUsers(userInfo.organization_id);
        }
      }
    } catch (error) {
      console.error("Error checking user role", error);
      setLoading(false);
    }
  };

  const fetchOrganizationsList = async () => {
    try {
      const response = await organizationsApi.getOrganizations();
      setOrganizations(response.data || []);
    } catch (error) {
      console.error("Error loading organizations", error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsApi.getOrganizations();
      const orgs = response.data || [];
      setOrganizations(orgs);
      // Default to "all" to show all users
      setSelectedOrgId("all");
      fetchUsers("all");
    } catch (error: any) {
      notify("Error loading organizations", { type: "error" });
      setLoading(false);
    }
  };

  const fetchUsers = async (orgId?: string) => {
    try {
      setLoading(true);
      // If "all" is selected or no orgId, fetch all users; otherwise filter by org
      const organizationId = orgId && orgId !== "all" ? orgId : undefined;
      const response = await userApi.getUsers({
        skip: 0,
        limit: 1000,
        organizationId: isSuperAdmin ? organizationId : undefined,
      });
      setUsers(response.data || []);
    } catch (error: any) {
      notify("Error loading users", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleOrgChange = (orgId: string) => {
    setSelectedOrgId(orgId);
    fetchUsers(orgId);
  };

  const getLevelChip = (user: UserRead) => {
    if (user.is_superuser && !user.organization_id) {
      return <Chip label="Super Admin" size="small" color="error" />;
    }
    if (user.is_superuser) {
      return <Chip label="Org Admin" size="small" color="error" />;
    }

    // Check permissions to determine level
    const permissions = user.role?.permissions || [];

    // Check if role has role management permissions
    const hasRoleManagement = permissions.some(
      (p) => p.resource === "role" && ["create", "edit", "delete"].includes(p.action)
    );

    // Check if role has user management permissions
    const hasUserManagement = permissions.some(
      (p) => p.resource === "user" && ["create", "edit"].includes(p.action)
    );

    if (hasRoleManagement) {
      // Has role management = Org Admin level
      return <Chip label="Org Admin" size="small" color="error" />;
    } else if (hasUserManagement) {
      // Has user management but not role management = Sub Admin level
      return <Chip label="Sub Admin" size="small" color="primary" />;
    } else {
      // No user or role management = User level
      return <Chip label="User" size="small" color="default" />;
    }
  };

  const getStatusChip = (isActive: boolean) => {
    return isActive ? (
      <Chip label="active" size="small" color="success" />
    ) : (
      <Chip label="inactive" size="small" color="default" />
    );
  };

  const canEditOrDeleteUser = (user: UserRead) => {
    // Super admin without org (platform-level) cannot be edited/deleted by anyone
    if (user.is_superuser && !user.organization_id) return false;

    // Super admin can edit/delete anyone
    if (isSuperAdmin) return true;

    // Sub Admin cannot edit/delete Org Admins or other Sub Admins
    if (isSubAdmin) {
      const targetUserRole = user.role?.name || "";
      if (targetUserRole === "Org Admin" || targetUserRole === "Sub Admin") {
        return false;
      }
    }

    // Org Admin can edit/delete anyone except super admins
    if (isOrgAdmin && !user.is_superuser) return true;

    return true;
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: UserRead) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    handleMenuClose();
    setOpenEdit(true);
  };

  const handleEditClose = () => {
    setOpenEdit(false);
    setSelectedUser(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setOpenDeleteDialog(true);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setSelectedUser(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    setDeleting(true);
    try {
      await userApi.deleteUser({ userId: selectedUser.id });
      notify("User deleted successfully", { type: "success" });
      setOpenDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers(selectedOrgId);
    } catch (error: any) {
      notify(error?.response?.data?.detail || "Error deleting user", { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId);
  const activeUsers = users.filter((u) => u.is_active);

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isSuperAdmin && organizations.length > 0 && (
            <FormControl sx={{ minWidth: 250 }} size="small">
              <InputLabel>Organization</InputLabel>
              <Select
                value={selectedOrgId}
                label="Organization"
                onChange={(e) => handleOrgChange(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Typography variant="body2" color="text.secondary">
            {users.length} total users ({activeUsers.length} active)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreate(true)}
        >
          Create User
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "14%" }}>Name</TableCell>
              <TableCell sx={{ width: "18%" }}>Email</TableCell>
              <TableCell sx={{ width: "14%" }}>Organization</TableCell>
              <TableCell sx={{ width: "10%" }}>Level</TableCell>
              <TableCell sx={{ width: "12%" }}>Role</TableCell>
              <TableCell sx={{ width: "12%" }}>Created By</TableCell>
              <TableCell sx={{ width: "10%" }}>Status</TableCell>
              <TableCell sx={{ width: "10%" }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <Typography variant="body2" noWrap>
                    {(user as any).full_name || user.email || "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <Typography variant="body2" noWrap>{user.email}</Typography>
                </TableCell>
                <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <Typography variant="body2" noWrap>
                    {organizations.find((o) => o.id === user.organization_id)?.name || "-"}
                  </Typography>
                </TableCell>
                <TableCell>{getLevelChip(user)}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {user.role?.name || "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {user.is_superuser ? "Super Admin" : "-"}
                  </Typography>
                </TableCell>
                <TableCell>{getStatusChip(user.is_active ?? false)}</TableCell>
                <TableCell align="right">
                  {canEditOrDeleteUser(user) ? (
                    <IconButton size="small" onClick={(e) => handleMenuClick(e, user)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      -
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedUser && canEditOrDeleteUser(selectedUser) && (
          <>
            <MenuItem onClick={handleEditClick}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Edit
            </MenuItem>
            <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Delete User
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the user <strong>"{(selectedUser as any)?.full_name || selectedUser?.email}"</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateUserModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSuccess={() => fetchUsers(selectedOrgId)}
        currentUserIsSuperAdmin={isSuperAdmin}
      />

      <EditUserModal
        open={openEdit}
        onClose={handleEditClose}
        onSuccess={() => fetchUsers(selectedOrgId)}
        user={selectedUser}
        organizationId={selectedOrgId}
      />
    </Box>
  );
};